import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";
import { buildOnlineCheckInQrUri } from "./online-check-in-qr-uri";
import {
  isPlausibleKimAddress,
  normalizeKimAddress,
} from "../../../../../lib/kim-address";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";

interface KimAddressQrCodeProps {
  kimAddress: string;
  practiceName?: string;
  size?: number;
  /**
   * The one instruction shown next to the code. Defaults to the statutory
   * wording ("App Ihrer Krankenkasse"); the private-insurance path passes its
   * own wording, because PKV insurers are not Krankenkassen (KC-82).
   */
  instruction?: string;
}

/**
 * Whether {@link KimAddressQrCode} will render a code for this address. Callers
 * use it to leave out the surrounding card and hint entirely instead of
 * rendering an empty frame when the address is implausible (KC-38).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * if (canRenderKimAddressQrCode(kimInfo?.kimAddress)) { … }
 * ```
 */
export function canRenderKimAddressQrCode(
  kimAddress: string | undefined,
): kimAddress is string {
  return kimAddress !== undefined && isPlausibleKimAddress(kimAddress);
}

/**
 * KIM Address QR Code Component
 *
 * A neutral, self-contained card with the practice's online check-in QR code
 * and one plain instruction. The patient never needs the KIM address itself
 * (the code carries it), so it appears only as a small, wrapping line for the
 * practice team (KC-04, KC-12, SPK-01). The card never grows wider than its
 * column. Renders nothing for an implausible address.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <KimAddressQrCode kimAddress="praxis@kim.example" />
 * ```
 */
export function KimAddressQrCode({
  kimAddress,
  practiceName,
  size = 200,
  instruction,
}: KimAddressQrCodeProps) {
  const { t } = useTranslation();

  // This QR decides where an insurer sends the patient's insurance data. An
  // implausible address cannot be a real mailbox, so showing a scannable code
  // for it would send that data nowhere while looking like it worked.
  if (!isPlausibleKimAddress(kimAddress)) {
    frontdeskDiagnosticLogger.warn(
      "[OnlineCheckIn] refusing to render a QR for an implausible KIM address",
    );
    return null;
  }

  const qrUri = buildOnlineCheckInQrUri(normalizeKimAddress(kimAddress));

  return (
    <div
      data-testid="frontdesk-kim-address-qr-code"
      className="flex w-full min-w-0 flex-col items-center gap-4 rounded-3xl border bg-card p-6 text-center text-foreground"
    >
      <div className="max-w-full rounded-2xl border bg-white p-4">
        <QRCodeSVG
          value={qrUri}
          size={size}
          level="M"
          fgColor="var(--foreground)"
          bgColor="transparent"
          className="h-auto max-w-full"
        />
      </div>

      <p className="m-0 flex max-w-[40ch] items-start gap-3 text-left text-lg leading-normal">
        <Smartphone aria-hidden className="mt-1 size-6 shrink-0 text-primary" />
        <span className="min-w-0">
          {instruction ?? t("self-service.check-in.qr-code.instruction-app")}
        </span>
      </p>

      <p className="m-0 w-full min-w-0 text-sm leading-snug break-all text-muted-foreground">
        {practiceName
          ? t("self-service.check-in.qr-code.staff-line-practice", {
              name: practiceName,
              address: kimAddress,
            })
          : t("self-service.check-in.qr-code.staff-line", {
              address: kimAddress,
            })}
      </p>
    </div>
  );
}
